import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSession } from '../state/useSession';
import { creditBalance, openBillingPortal } from '../api/billing';
import { PRIVACY_URL, SUPPORT_EMAIL, TERMS_URL } from '@/config/legal';
import type { Tier } from '@/policy/limits';
import { deletionAlertTitle, type DeletionOutcome } from '@/account/deleteAccount';
import {
  DELETE_ACCOUNT_TITLE,
  DELETE_ACCOUNT_WARNING,
  planSummary,
} from '@/components/account/accountView';
import { Button, ListRow, Screen, SectionLabel } from '../ui';
import RedeemCode, { type Redeemed } from './account/RedeemCode';
import ChangePassword from './account/ChangePassword';
import { deleteAccountEverywhere } from './account/deleteAccount';

const TIERS: readonly Tier[] = ['free', 'paid', 'unlimited'];

/**
 * Ported from src/app/(main)/account.tsx, section for section and in the same words: Security,
 * Help, Subscription, Credits, Admin, Danger zone.
 *
 * Two of the phone's sections are not here. AI data sharing is the phone's consent record, kept
 * on the device; the web has no such gate yet. Blocked creators belongs to Discover.
 *
 * Credits is where the web goes FURTHER than the phone: the iOS app no longer shows Redeem code
 * (App Store 3.1.1), so this is where codes are redeemed.
 */
export default function Account() {
  const navigate = useNavigate();
  const { user, signOut } = useSession();
  const dialog = useRef<HTMLDialogElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalNotice, setPortalNotice] = useState<string | null>(null);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [deletion, setDeletion] = useState<Exclude<DeletionOutcome, { status: 'deleted' }> | null>(null);
  // Unknown until the server answers; unknown shows nothing rather than a wrong balance.
  const [tier, setTier] = useState<Tier | null>(null);
  const [credits, setCredits] = useState<number | null>(null);

  useEffect(() => {
    let live = true;
    void creditBalance()
      .then((b) => {
        if (!live) return;
        setCredits(b.creditsRemaining);
        setTier(TIERS.includes(b.tier as Tier) ? (b.tier as Tier) : 'free');
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  function redeemed(result: Redeemed): void {
    if (typeof result.creditsRemaining === 'number') setCredits(result.creditsRemaining);
    if (result.tier && TIERS.includes(result.tier as Tier)) setTier(result.tier as Tier);
  }

  /**
   * The phone's deletion, outcome for outcome (`runAccountDeletion`): data first, then the login,
   * and nothing rounded up to success. A clean deletion signs out and leaves. Anything else is
   * said, under the phone's title for it — and when the account IS closed, signing out waits for
   * the person to have read why their data is not all gone.
   */
  async function reallyDelete(): Promise<void> {
    setBusy(true);
    setError(null);
    // A retry after "Nothing was deleted" must not leave that sentence up over the new attempt.
    setDeletion(null);
    try {
      const outcome = await deleteAccountEverywhere();
      dialog.current?.close();
      if (outcome.status === 'deleted') {
        await leave();
        return;
      }
      setDeletion(outcome);
    } finally {
      setBusy(false);
    }
  }

  async function leave(): Promise<void> {
    await signOut().catch(() => undefined);
    navigate('/', { replace: true });
  }

  /**
   * Out through the same door they came in.
   *
   * Someone who subscribed before their Stripe customer id was stored is found by an email
   * lookup on the first visit, so the first press can take a few seconds — which is why
   * the row shows it is working rather than looking ignored.
   */
  async function manageSubscription(): Promise<void> {
    setPortalBusy(true);
    setPortalNotice(null);
    setError(null);
    try {
      const result = await openBillingPortal();
      if (result.status === 'managed_elsewhere') {
        // Not a failure: they pay through the App Store, so this is simply the wrong door.
        setPortalNotice('You subscribed through the App Store, so your subscription is managed there — on your iPhone, in Settings › Apple Account › Subscriptions.');
      } else if (result.status === 'signin_required') {
        setError('Sign in again to manage your subscription.');
      } else if (result.status === 'error') {
        setError(result.message);
      }
      // 'redirecting' needs nothing: the browser is already on its way to Stripe.
    } catch {
      /*
       * `openBillingPortal` answers with a result rather than throwing, so this should be
       * unreachable — which is exactly why it is here. A row that resets to "Manage
       * subscription" having silently done nothing is indistinguishable from one that was
       * never pressed.
       */
      setError('Could not open the billing portal. Try again in a moment.');
    } finally {
      setPortalBusy(false);
    }
  }

  const accountClosed = deletion?.status === 'deleted_with_remainder';

  return (
    <Screen title="Account">
      {user?.email && <p className="type-body project-meta">{user.email}</p>}
      {tier !== null && credits !== null && (
        <p className="type-caption plan-detail">{planSummary(tier, credits)}</p>
      )}

      <SectionLabel>Security</SectionLabel>
      <div className="card clay-card detail-rows">
        <ListRow icon="password" label="Change password" onPress={() => setPasswordOpen(true)} divider />
        <ListRow icon="signOut" label="Sign out" accent onPress={() => void signOut().then(() => navigate('/'))} />
      </div>

      {/* App Review 1.2 on the phone, and plain decency here: somebody something has gone wrong
          for needs an address they can read, not only a link that may open nothing. */}
      <SectionLabel>Help</SectionLabel>
      <div className="card clay-card detail-rows">
        <ListRow
          icon="account"
          label="Contact support"
          onPress={() => window.location.assign(`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Saddle support')}`)}
          divider
        />
        <ListRow icon="account" label="Terms of use" onPress={() => window.open(TERMS_URL, '_blank', 'noreferrer')} divider />
        <ListRow icon="account" label="Privacy policy" onPress={() => window.open(PRIVACY_URL, '_blank', 'noreferrer')} />
      </div>
      <p className="type-caption plan-detail">Write to {SUPPORT_EMAIL}</p>

      <SectionLabel>Subscription</SectionLabel>
      <div className="card clay-card detail-rows">
        <ListRow
          icon="account"
          label={portalBusy ? 'Opening…' : 'Manage subscription'}
          onPress={() => void manageSubscription()}
          divider
        />
        {/* Web only: the browser buys credits through Stripe Checkout on that screen. */}
        <ListRow icon="password" label="Credits" onPress={() => navigate('/credits')} />
      </div>
      {portalNotice && <p role="status" className="type-caption plan-detail">{portalNotice}</p>}

      <SectionLabel>Credits</SectionLabel>
      <RedeemCode tier={tier} onRedeemed={redeemed} />

      {tier === 'unlimited' && (
        <>
          <SectionLabel>Admin</SectionLabel>
          <div className="card clay-card detail-rows">
            <ListRow icon="llmSettings" label="Analytics" onPress={() => navigate('/admin/metrics')} />
          </div>
        </>
      )}

      <SectionLabel>Danger zone</SectionLabel>
      <div className="card clay-card detail-rows">
        <ListRow icon="delete" label="Delete account" destructive onPress={() => dialog.current?.showModal()} />
      </div>

      {error && <p role="alert" className="type-caption field-error-text">{error}</p>}

      {deletion && (
        <div role="alert" className="card clay-card">
          <h2 className="type-title">{deletionAlertTitle(deletion.status)}</h2>
          <p className="type-body">{deletion.message}</p>
          {accountClosed && <Button title="OK" onPress={() => void leave()} />}
        </div>
      )}

      {/* A <dialog>, not window.confirm: a native modal blocks the page and cannot be tested. */}
      <dialog ref={dialog} aria-label="Delete your account" className="confirm-dialog">
        <h2 className="type-title">{DELETE_ACCOUNT_TITLE}</h2>
        {DELETE_ACCOUNT_WARNING.split('\n\n').map((paragraph) => (
          <p key={paragraph} className="type-body">{paragraph}</p>
        ))}
        <div className="build-actions">
          <Button title="Delete" variant="dangerSoft" loading={busy} onPress={() => void reallyDelete()} />
          <Button title="Cancel" variant="ghost" onPress={() => dialog.current?.close()} />
        </div>
      </dialog>

      <ChangePassword open={passwordOpen} email={user?.email ?? ''} onClose={() => setPasswordOpen(false)} />
    </Screen>
  );
}
